import { useEffect, useMemo, useState } from "react";
import { Gift, Plus, Search, Pencil, Copy, Archive, ArchiveRestore, Trash2, Download, Users, X } from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import PackageModal from "./PackageModal";
import { mastersApi, apiErrorMessage } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { formatCurrencyFull } from "@/lib/data";
import { byNewest } from "@/lib/utils";
import { MARGIN_WARN_PCT, packagePrice } from "@/lib/pricing";
import type { PackagePayload } from "./types";

type SortKey = "newest" | "name" | "price" | "margin" | "used";

const SORTS: { value: SortKey; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "name", label: "Name (A–Z)" },
  { value: "price", label: "Price (high to low)" },
  { value: "margin", label: "Margin (low to high)" },
  { value: "used", label: "Most used" },
];

const MANAGER_ROLES = ["super_admin", "admin", "master", "manager"];

/** Price at the package's smallest guest count, what each extra guest adds, and the margin. */
function quote(pkg: PackagePayload) {
  const guests = pkg.min_guests || 1;
  const lines = pkg.services_data;
  const hasLines = lines.length > 0;
  const at = packagePrice(lines, guests, pkg.discount_type, pkg.discount_value);
  const next = packagePrice(lines, guests + 1, pkg.discount_type, pkg.discount_value);
  const perGuest = lines.some((l) => l.unit === "guest" && !l.is_optional);
  return {
    guests,
    price: hasLines ? at.price : Number(pkg.offered_price || pkg.base_price) || 0,
    extraGuest: perGuest ? next.price - at.price : 0,
    perGuest,
    pct: at.pct,
    level: hasLines ? at.level : ("none" as const),
  };
}

const usedCount = (p: PackagePayload) => (p.events_count || 0) + (p.quotations_count || 0);

export default function PackagesPage() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const canManage = MANAGER_ROLES.includes(user?.role || "");

  const [packages, setPackages] = useState<PackagePayload[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [sort, setSort] = useState<SortKey>("newest");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PackagePayload | null>(null);
  const [deleting, setDeleting] = useState<PackagePayload | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await mastersApi.packages.list();
      setPackages(res?.data?.data ?? res?.data ?? []);
    } catch (err) {
      showToast(apiErrorMessage(err, "Couldn't load packages"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const archivedCount = packages.filter((p) => !p.is_active).length;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = packages.filter((p) => {
      if (!showArchived && !p.is_active) return false;
      if (!q) return true;
      return [p.name, p.description, ...(p.event_types || []), ...p.services_data.map((l) => l.name)]
        .some((v) => (v || "").toLowerCase().includes(q));
    });
    return [...list].sort((a, b) => {
      switch (sort) {
        case "name": return a.name.localeCompare(b.name);
        case "price": return quote(b).price - quote(a).price;
        case "margin": return quote(a).pct - quote(b).pct;
        case "used": return usedCount(b) - usedCount(a);
        default: return byNewest(a, b);
      }
    });
  }, [packages, search, showArchived, sort]);

  // Selection only covers what's on screen
  useEffect(() => { setSelected(new Set()); }, [search, showArchived, sort]);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (p: PackagePayload) => { setEditing(p); setModalOpen(true); };

  const setActive = async (ids: number[], active: boolean) => {
    if (!ids.length || busy) return;
    setBusy(true);
    try {
      // Partial update keeps the package's service lines
      await Promise.all(ids.map((id) => mastersApi.packages.update(id, { is_active: active })));
      showToast(ids.length === 1 ? (active ? "Package restored" : "Package archived") : `${ids.length} packages ${active ? "restored" : "archived"}`, "success");
      setSelected(new Set());
      await load();
    } catch (err) {
      showToast(apiErrorMessage(err, "Couldn't update the package"), "error");
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async (p: PackagePayload) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await mastersApi.packages.duplicate(p.id);
      showToast(res?.message || "Package copied", "success");
      await load();
      // Open the copy straight away so it can be renamed and adjusted
      if (res?.data) { setShowArchived(true); openEdit(res.data as PackagePayload); }
    } catch (err) {
      showToast(apiErrorMessage(err, "Couldn't copy the package"), "error");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await mastersApi.packages.delete(deleting.id);
      showToast("Package deleted", "success");
      setDeleting(null);
      await load();
    } catch (err) {
      showToast(apiErrorMessage(err, "Couldn't delete the package"), "error");
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [
      ["Package", "Status", "Guests", "Event types", "Included services", "Add-ons", "Price at min guests", "Min guests", "Per extra guest", "Discount", "Margin %", "Quotations", "Events"],
      ...visible.map((p) => {
        const q = quote(p);
        return [
          p.name, p.is_active ? "Active" : "Archived",
          p.min_guests || p.max_guests ? `${p.min_guests ?? ""}–${p.max_guests ?? ""}` : "",
          (p.event_types || []).join(", "),
          p.services_data.filter((l) => !l.is_optional).length,
          p.services_data.filter((l) => l.is_optional).length,
          Math.round(q.price), q.guests, Math.round(q.extraGuest),
          p.discount_type ? (p.discount_type === "percent" ? `${p.discount_value}%` : `₹${p.discount_value}`) : "",
          q.level === "none" ? "" : q.pct.toFixed(1),
          p.quotations_count, p.events_count,
        ];
      }),
    ];
    const blob = new Blob(["﻿" + rows.map((r) => r.map(esc).join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `packages_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const marginPill = (level: string, pct: number) =>
    level === "none" ? null : (
      <span className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-bold ${level === "loss" ? "bg-red-50 text-red-700" : level === "low" ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-700"}`}
        title={level === "low" ? `Below ${MARGIN_WARN_PCT}%` : undefined}>
        {level === "loss" ? "Loss" : `${pct.toFixed(0)}% margin`}
      </span>
    );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Toolbar */}
      <div className="flex flex-col gap-3 mb-4 lg:flex-row lg:items-center">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search packages, services or event types…" aria-label="Search packages"
            className="w-full h-10 pl-9 pr-9 rounded-xl border border-stone-200 text-sm bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none" />
          {search && (
            <button type="button" onClick={() => setSearch("")} aria-label="Clear search" className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg flex items-center justify-center text-stone-400 hover:bg-stone-100">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label="Sort packages"
            className="h-10 px-3 rounded-xl border border-stone-200 text-sm bg-white text-stone-700 outline-none focus:border-amber-400">
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {archivedCount > 0 && (
            <label className="h-10 inline-flex items-center gap-2 px-3 rounded-xl border border-stone-200 bg-white text-sm text-stone-600 cursor-pointer">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="w-4 h-4 accent-amber-600" />
              Show archived ({archivedCount})
            </label>
          )}
          <button type="button" onClick={exportCsv} disabled={visible.length === 0}
            className="h-10 inline-flex items-center gap-1.5 px-3 rounded-xl border border-stone-200 bg-white text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">
            <Download className="w-4 h-4" /> CSV
          </button>
          {canManage && (
            <button type="button" onClick={openNew} className="h-10 inline-flex items-center gap-2 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold">
              <Plus className="w-4 h-4" /> New package
            </button>
          )}
        </div>
      </div>

      {/* Bulk bar */}
      {canManage && selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
          <span className="font-semibold text-amber-900 mr-auto">{selected.size} selected</span>
          <button type="button" disabled={busy} onClick={() => setActive([...selected], false)} className="h-9 px-3 rounded-lg bg-white border border-amber-200 text-stone-700 hover:bg-amber-100 disabled:opacity-50">Archive</button>
          <button type="button" disabled={busy} onClick={() => setActive([...selected], true)} className="h-9 px-3 rounded-lg bg-white border border-amber-200 text-stone-700 hover:bg-amber-100 disabled:opacity-50">Restore</button>
          <button type="button" onClick={() => setSelected(new Set())} className="h-9 px-3 rounded-lg text-stone-600 hover:bg-amber-100">Clear</button>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-stone-200">
          <Gift className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-600 font-medium">{packages.length === 0 ? "No packages yet" : "No packages match"}</p>
          {packages.length === 0 && canManage ? (
            <button type="button" onClick={openNew} className="mt-4 h-10 inline-flex items-center gap-2 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold">
              <Plus className="w-4 h-4" /> Create the first package
            </button>
          ) : (
            <button type="button" onClick={() => { setSearch(""); setShowArchived(true); }} className="mt-3 text-sm font-semibold text-amber-700 hover:underline">Clear search and show archived</button>
          )}
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((p) => {
            const q = quote(p);
            const included = p.services_data.filter((l) => !l.is_optional).length;
            const addOns = p.services_data.length - included;
            const used = usedCount(p);
            return (
              <article key={p.id} className={`bg-white rounded-xl border p-4 flex flex-col gap-3 ${selected.has(p.id) ? "border-amber-400 ring-2 ring-amber-100" : "border-stone-200"} ${p.is_active ? "" : "opacity-75"}`}>
                <div className="flex items-start gap-3">
                  {canManage && (
                    <input type="checkbox" checked={selected.has(p.id)} aria-label={`Select ${p.name}`}
                      onChange={() => setSelected((prev) => { const next = new Set(prev); if (next.has(p.id)) next.delete(p.id); else next.add(p.id); return next; })}
                      className="mt-1 w-4 h-4 accent-amber-600 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-bold text-stone-800 break-words">{p.name}</h3>
                      {p.is_active ? marginPill(q.level, q.pct) : <span className="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-600">Archived</span>}
                    </div>
                    {p.description && <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{p.description}</p>}
                  </div>
                </div>

                <div>
                  <p className="text-xl font-bold text-stone-900 tabular-nums">{formatCurrencyFull(q.price)}</p>
                  <p className="text-xs text-stone-500">
                    {q.perGuest ? `for ${q.guests} guest${q.guests === 1 ? "" : "s"} · + ${formatCurrencyFull(q.extraGuest)} per extra guest` : "fixed price"}
                    {p.discount_type && p.discount_value > 0 && ` · ${p.discount_type === "percent" ? `${p.discount_value}%` : formatCurrencyFull(p.discount_value)} off`}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 text-xs text-stone-500">
                  {(p.event_types || []).map((t) => <span key={t} className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 font-semibold">{t}</span>)}
                  {(p.min_guests || p.max_guests) && (
                    <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" />{p.min_guests ?? "?"}–{p.max_guests ?? "any"} guests</span>
                  )}
                  <span>· {included} service{included === 1 ? "" : "s"}{addOns ? ` + ${addOns} add-on${addOns === 1 ? "" : "s"}` : ""}</span>
                  {used > 0 && <span>· used in {p.quotations_count} quotation{p.quotations_count === 1 ? "" : "s"}, {p.events_count} event{p.events_count === 1 ? "" : "s"}</span>}
                </div>

                {canManage && (
                  <div className="mt-auto pt-3 border-t border-stone-100 flex flex-wrap gap-2">
                    <button type="button" onClick={() => openEdit(p)} className="h-10 px-3 inline-flex items-center gap-1.5 rounded-lg border border-stone-200 text-sm text-stone-700 hover:bg-amber-50 hover:border-amber-300">
                      <Pencil className="w-4 h-4" /> Edit
                    </button>
                    <button type="button" disabled={busy} onClick={() => duplicate(p)} className="h-10 px-3 inline-flex items-center gap-1.5 rounded-lg border border-stone-200 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                      <Copy className="w-4 h-4" /> Duplicate
                    </button>
                    <button type="button" disabled={busy} onClick={() => setActive([p.id], !p.is_active)} className="h-10 px-3 inline-flex items-center gap-1.5 rounded-lg border border-stone-200 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                      {p.is_active ? <><Archive className="w-4 h-4" /> Archive</> : <><ArchiveRestore className="w-4 h-4" /> Restore</>}
                    </button>
                    {used === 0 && (
                      <button type="button" onClick={() => setDeleting(p)} aria-label={`Delete ${p.name}`} className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-stone-200 text-stone-500 hover:text-red-600 hover:bg-red-50 ml-auto">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      <PackageModal isOpen={modalOpen} pkg={editing} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={load} />

      <DeleteConfirmationModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Delete package"
        message={`Delete “${deleting?.name}”? It has never been used, so nothing else changes. This can't be undone.`}
        loading={busy}
      />
    </DashboardLayout>
  );
}
